import React, { MutableRefObject, useContext, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { Button } from "react-bootstrap";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { ProductContactApi } from "src/services/Api/Organization/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ListData } from "go-list/list";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import ProductContext from "../../../pages/Index/pages/Edit";
import { ContactModalState } from "../../../services/types";
import { ProductContactModal } from "./ProductContactModal";

const buildConfig = (
	t: TFunction,
	setDisplayContactModal: (contactModalState: ContactModalState) => void,
	contactModalDataRef: MutableRefObject<ProductContactApi | undefined>,
	listParams: MutableRefObject<Record<string, any> | undefined>,
	organization: OrganizationState,
	productId?: number
) => {
	const config = {
		fields: [
			{
				id: "externalName",
				name: t("common.word.name", { ns: "lib" }),
				render: (item: ProductContactApi) => item.contact.name,
			},
			{
				id: "reference_price",
				name: t("modules.product.field.default_price_net_money.title"),
				render: (item: ProductContactApi) => FormatMoney(item.reference_price),
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				click: (item: ProductContactApi) => {
					contactModalDataRef.current = item;
					setDisplayContactModal(ContactModalState.UPDATE);
				},
			},
		],
		hideFilters: true,
		selectedColumns: ["externalName", "reference_price"],
		exportConfig: {
			title: t("modules.contact.field.export_config.title"),
			filename: t("modules.contact.header.title"),
			organization: organization.name,
		},
		fetch: async (params: Record<string, any>, sourceToken?: CancelTokenSource) => {
			const newParams = { ...params, include: "contact" };
			listParams.current = newParams;
			if (!productId) return undefined;
			return apiOrganization.getProductContacts(productId, newParams, { cancelToken: sourceToken?.token });
		},
	};

	return config;
};
export const OrganizationProductContactIndexPage = () => {
	const { t } = useTranslation();
	const [responseData, setResponseData] = useState<ProductContactApi[] | undefined>(undefined);
	const [contactModalState, setContactModalState] = useState<ContactModalState>(ContactModalState.CLOSED);
	const contactModalDataRef = useRef<ProductContactApi | undefined>(undefined);
	const listParams = useRef<Record<string, any>>({});
	const productContext = useContext(ProductContext);
	const productId = productContext.product?.id;
	const organization = useSelector(selectOrganization);

	const config = buildConfig(t, setContactModalState, contactModalDataRef, listParams, organization, productId);

	const onRefresh = async () => {
		if (config.fetch) {
			const newListData = await config.fetch(listParams.current);
			setResponseData(newListData);
		}
	};

	return (
		<>
			{contactModalState !== ContactModalState.CLOSED && productId && (
				<ProductContactModal
					contactModalState={contactModalState}
					setContactModalState={setContactModalState}
					contactModalDataRef={contactModalDataRef}
					contactModalData={contactModalDataRef.current}
					productId={productId}
					onRefresh={onRefresh}
				/>
			)}
			<ListData
				data={responseData}
				config={config}
				onFetch={(items: ProductContactApi[]) => setResponseData(items)}
				customActions={
					<Button onClick={() => setContactModalState(ContactModalState.NEW)}>
						{t("modules.contact.action.add.title")}
					</Button>
				}
				displayListFeaturesOnEmptyList
				withoutSegments
			/>
		</>
	);
};
